import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';
import EmailInputForm from './components/EmailInputForm';
import SuccessState from './components/SuccessState';

const ForgotPasswordRequest = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(value);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError('Please enter your email address.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);

    try {
      // Use Supabase built-in password reset — sends magic link to user
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email?.trim(), {
        redirectTo: `${window.location?.origin}/reset-password`
      });

      if (resetError) {
        throw resetError;
      }

      // Also send branded email via Resend edge function
      try {
        await supabase?.functions?.invoke('send-password-reset-email', {
          body: {
            email: email?.trim(),
            resetUrl: `${window.location?.origin}/reset-password`
          }
        });
      } catch (resendErr) {
        // Non-blocking — Supabase already sent the reset link
        console.warn('Resend notification failed (non-blocking):', resendErr?.message);
      }

      setIsSuccess(true);
    } catch (err) {
      if (err?.message?.includes('rate limit') || err?.message?.includes('too many')) {
        setError('Too many reset attempts. Please wait a few minutes before trying again.');
      } else {
        // Don't reveal whether email exists for security
        setIsSuccess(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="Scale" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-text-primary mb-2">LawDesk KE</h1>
          <p className="text-text-secondary">Legal Practice Management System</p>
        </div>

        {/* Card */}
        <div className="bg-surface rounded-lg shadow-card border border-border p-8">
          {isSuccess ? (
            <SuccessState email={email} />
          ) : (
            <EmailInputForm
              email={email}
              setEmail={setEmail}
              error={error}
              isLoading={isLoading}
              onSubmit={handleSubmit}
            />
          )}
        </div>

        {/* Back to Login */}
        <div className="text-center mt-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-primary transition-smooth"
          >
            <Icon name="ArrowLeft" size={16} />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordRequest;